import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import { DEFAULT_CONSENT } from "@/lib/consent";

const CATEGORIES = [
  {
    key: "necessary",
    icon: ShieldCheck,
    title: "Cookie essenziali",
    always: true,
    desc:
      "Indispensabili al funzionamento del sito (autenticazione, sicurezza, salvataggio preferenze). Non richiedono consenso e non possono essere disattivati.",
  },
  {
    key: "statistics",
    icon: BarChart3,
    title: "Statistiche e misurazione",
    desc:
      "Ci aiutano a capire quali pagine funzionano meglio e come migliorare l'esperienza. Strumenti: Google Analytics 4 (con IP anonimizzato).",
  },
  {
    key: "marketing",
    icon: Megaphone,
    title: "Marketing e remarketing",
    desc:
      "Servono a valutare l'efficacia delle campagne pubblicitarie e a mostrarti annunci pertinenti su Google e Meta. Strumenti: Google Ads, Meta Pixel.",
  },
];

/**
 * Granular preferences dialog. Toggles per-category, save button
 * writes to localStorage and pushes to Google Consent Mode v2.
 */
export function ConsentPreferences({ open, initial, onClose, onSave }) {
  const [state, setState] = useState(initial || DEFAULT_CONSENT);

  useEffect(() => {
    if (open) setState({ ...DEFAULT_CONSENT, ...(initial || {}) });
  }, [open, initial]);

  const toggle = (key) => (e) => {
    if (key === "necessary") return;
    setState((s) => ({ ...s, [key]: e.target.checked }));
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[75] bg-foreground/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            data-testid="consent-prefs-scrim"
          />
          <motion.div
            role="dialog"
            aria-label="Personalizza cookie"
            data-testid="consent-prefs-dialog"
            className="fixed inset-x-4 sm:inset-x-auto sm:right-6 sm:left-6 md:left-auto md:right-6 bottom-4 sm:top-1/2 sm:-translate-y-1/2 z-[80] max-w-lg sm:mx-auto bg-background text-foreground shadow-2xl border border-foreground/10"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 0.9, 0.24, 1] }}
          >
            <div className="flex items-start justify-between p-6 sm:p-8 pb-4">
              <div>
                <p className="overline">Preferenze cookie</p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl leading-tight">
                  Scegli quali <span className="italic text-primary">categorie</span> attivare.
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Chiudi"
                data-testid="consent-prefs-close"
                className="p-2 -mr-2 -mt-2 hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 sm:px-8 pb-4 space-y-4 max-h-[55vh] overflow-y-auto">
              {CATEGORIES.map(({ key, icon: Icon, title, desc, always }) => (
                <label
                  key={key}
                  className={`block border border-foreground/10 p-4 sm:p-5 cursor-pointer transition-colors ${
                    always ? "bg-muted/40 cursor-default" : "hover:border-primary/40"
                  }`}
                  data-testid={`consent-cat-${key}`}
                >
                  <div className="flex items-start gap-4">
                    <Icon className="h-4 w-4 mt-1 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-display text-base">{title}</p>
                        {always ? (
                          <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-primary">
                            Sempre attivi
                          </span>
                        ) : (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            checked={!!state[key]}
                            onChange={toggle(key)}
                            data-testid={`consent-toggle-${key}`}
                            aria-label={title}
                          />
                        )}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                        {desc}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="border-t border-foreground/10 p-6 sm:p-8 flex flex-wrap gap-3 justify-end">
              <button
                type="button"
                onClick={() => onSave({ statistics: false, marketing: false })}
                data-testid="consent-prefs-reject"
                className="btn-base border border-foreground/25 text-foreground px-6 py-3 hover:bg-foreground hover:text-background text-sm"
              >
                Rifiuta tutti
              </button>
              <button
                type="button"
                onClick={() => onSave(state)}
                data-testid="consent-prefs-save"
                className="btn-base bg-foreground text-background px-6 py-3 hover:bg-primary text-sm"
              >
                Salva le mie scelte
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
